import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import { Printer, Clock, DollarSign, Search, Calendar } from 'lucide-react';
import axios from 'axios';

import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { API } from '../../contexts/AuthContext';
import { getErrorMessage } from '../../utils/errorHandler';
import { formatAmount } from '../../utils/format';

const OrderHistoryModal = ({ isOpen, onClose, getAuthHeaders, tenantInfo }) => {
  const [paidOrders, setPaidOrders] = useState([]);
  const [filteredOrders, setFilteredOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [customerSearch, setCustomerSearch] = useState('');
  
  // Date range filters
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  const loadPaidOrders = useCallback(async () => {
    setLoading(true);
    try {
      // Build query string manually to ensure correct format: status=delivered
      // El backend ordena los estados terminales del más nuevo al más viejo; el limite
      // alto (solo al abrir el diálogo, no en polling) deja margen para el filtro de
      // fechas: con la ventana corta de antes se perdían los cobros recientes.
      const response = await axios.get(`${API}/orders?status=delivered&limit=500`, {
        headers: getAuthHeaders()
      });
      
      // Filtrar solo órdenes pagadas
      const allPaidOrders = (Array.isArray(response.data) ? response.data : []).filter(order => {
        return order.payment_status === 'paid';
      });
      
      // Sort by payment date, most recent first
      allPaidOrders.sort((a, b) => new Date(b.updated_at) - new Date(a.updated_at));
      
      setPaidOrders(allPaidOrders);
      setFilteredOrders(allPaidOrders);
    } catch (error) {
      toast.error(getErrorMessage(error, 'Error al cargar historial de órdenes'));
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);

  useEffect(() => {
    if (isOpen) {
      // NO establecer fechas por defecto - mostrar todas las órdenes
      setStartDate('');
      setEndDate('');
      loadPaidOrders();
    }
  }, [isOpen, loadPaidOrders]);

  // Filter orders by customer name/phone and date range
  useEffect(() => {
    let filtered = [...paidOrders];
    
    // Filtrar por rango de fechas
    if (startDate || endDate) {
      filtered = filtered.filter(order => {
        // Usar solo la fecha (sin horas) para comparar
        const orderDate = new Date(order.updated_at);
        const orderDateOnly = new Date(orderDate.getFullYear(), orderDate.getMonth(), orderDate.getDate());
        
        if (startDate && endDate) {
          const start = new Date(startDate);
          const startDateOnly = new Date(start.getFullYear(), start.getMonth(), start.getDate());
          const end = new Date(endDate);
          const endDateOnly = new Date(end.getFullYear(), end.getMonth(), end.getDate());
          
          return orderDateOnly >= startDateOnly && orderDateOnly <= endDateOnly;
        } else if (startDate) {
          const start = new Date(startDate);
          const startDateOnly = new Date(start.getFullYear(), start.getMonth(), start.getDate());
          return orderDateOnly >= startDateOnly;
        } else if (endDate) {
          const end = new Date(endDate);
          const endDateOnly = new Date(end.getFullYear(), end.getMonth(), end.getDate());
          return orderDateOnly <= endDateOnly;
        }
        return true;
      });
    }
    
    // Filtrar por nombre o teléfono del cliente
    if (customerSearch.trim()) {
      const search = customerSearch.toLowerCase();
      filtered = filtered.filter(order => 
        order.customer_name?.toLowerCase().includes(search) ||
        order.customer_phone?.includes(search)
      );
    }
    
    setFilteredOrders(filtered);
  }, [customerSearch, paidOrders, startDate, endDate]);

  const printReceipt = (order, isReprint = false) => {
    const printWindow = window.open('', '_blank');
    
    const orderTypeNames = {
      'dine_in': 'En Local',
      'takeout': 'Para Llevar',
      'delivery': 'Delivery'
    };
    
    const paymentMethodNames = {
      'cash': 'Efectivo',
      'card': 'Tarjeta',
      'transfer': 'Transferencia'
    };

    // Restaurant info from tenant
    const restaurantName = tenantInfo?.name || 'Restaurante';
    const restaurantAddress = tenantInfo?.address || '';
    const restaurantPhone = tenantInfo?.phone || '';
    const restaurantEmail = tenantInfo?.email || '';
    // Porcentaje real de ITBIS de la orden (order.tax ya viene calculado por el
    // backend). Antes se imprimía la tasa decimal (ej. "0.18%"), ahora el % entero.
    const taxPct = order.subtotal
      ? Math.round(((order.tax || 0) / order.subtotal) * 100)
      : Math.round((tenantInfo?.tax_rate || 0) * 100);
    
    const itemsHTML = order.items?.map(item => `
      <tr>
        <td>${item.quantity}</td>
        <td>${item.product_name}</td>
        <td class="price">$${formatAmount(item.subtotal)}</td>
      </tr>
    `).join('') || '';
    
    const reprintBanner = isReprint ? `
      <div class="reprint-banner">
        ⚠️ REIMPRESIÓN
      </div>
    ` : '';
    
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
      <head>
        <title>Recibo #${order.order_number}</title>
        <style>
          body {
            font-family: 'Courier New', monospace;
            width: 80mm;
            margin: 0 auto;
            padding: 10px;
            font-size: 11px;
          }
          .reprint-banner {
            text-align: center;
            background: #000;
            color: #fff;
            padding: 8px;
            margin-bottom: 10px;
            font-weight: bold;
            font-size: 12px;
            border: 2px solid #000;
          }
          .header {
            text-align: center;
            margin-bottom: 15px;
          }
          .header h1 {
            font-size: 16px;
            margin: 0 0 5px 0;
            font-weight: bold;
          }
          .header .restaurant-info {
            font-size: 9px;
            color: #333;
          }
          .header .restaurant-info p {
            margin: 2px 0;
          }
          .divider {
            border-top: 1px dashed #000;
            margin: 10px 0;
          }
          .info {
            margin: 10px 0;
          }
          .info p {
            margin: 3px 0;
          }
          table {
            width: 100%;
            border-collapse: collapse;
          }
          th, td {
            text-align: left;
            padding: 3px 0;
          }
          .price {
            text-align: right;
          }
          .total-section {
            margin-top: 10px;
            padding-top: 10px;
            border-top: 1px dashed #000;
          }
          .total-row {
            display: flex;
            justify-content: space-between;
            margin: 3px 0;
          }
          .total-row.grand-total {
            font-size: 14px;
            font-weight: bold;
            margin-top: 5px;
            padding-top: 5px;
            border-top: 2px solid #000;
          }
          .footer {
            text-align: center;
            margin-top: 15px;
            padding-top: 10px;
            border-top: 1px dashed #000;
            font-size: 10px;
          }
          @media print {
            body { width: 100%; margin: 0; padding: 5px; }
          }
        </style>
      </head>
      <body>
        ${reprintBanner}
        
        <div class="header">
          <h1>${restaurantName}</h1>
          <div class="restaurant-info">
            ${restaurantAddress ? `<p>📍 ${restaurantAddress}</p>` : ''}
            ${restaurantPhone ? `<p>📞 ${restaurantPhone}</p>` : ''}
            ${restaurantEmail ? `<p>✉️ ${restaurantEmail}</p>` : ''}
          </div>
        </div>
        
        <div class="divider"></div>
        
        <div class="info">
          <p><strong>Factura:</strong> #${order.order_number}</p>
          <p><strong>Fecha:</strong> ${new Date(order.created_at).toLocaleString('es-ES')}</p>
          <p><strong>Tipo:</strong> ${orderTypeNames[order.order_type] || order.order_type}</p>
          ${order.table_name ? `<p><strong>Mesa:</strong> ${order.table_name}</p>` : ''}
          ${order.customer_name ? `<p><strong>Cliente:</strong> ${order.customer_name}</p>` : ''}
          ${order.customer_phone ? `<p><strong>Teléfono:</strong> ${order.customer_phone}</p>` : ''}
          ${order.payment_method ? `<p><strong>Pago:</strong> ${paymentMethodNames[order.payment_method] || order.payment_method}</p>` : ''}
        </div>
        
        <div class="divider"></div>
        
        <table>
          <thead>
            <tr>
              <th>Cant</th>
              <th>Descripción</th>
              <th class="price">Precio</th>
            </tr>
          </thead>
          <tbody>
            ${itemsHTML}
          </tbody>
        </table>
        
        <div class="total-section">
          <div class="total-row">
            <span>Subtotal:</span>
            <span>$${formatAmount(order.subtotal || order.total)}</span>
          </div>
          ${order.tax ? `
          <div class="total-row">
            <span>ITBIS (${taxPct}%):</span>
            <span>$${formatAmount(order.tax)}</span>
          </div>
          ` : ''}
          ${order.discount ? `
          <div class="total-row">
            <span>Descuento:</span>
            <span>-$${formatAmount(order.discount)}</span>
          </div>
          ` : ''}
          <div class="total-row grand-total">
            <span>TOTAL:</span>
            <span>$${formatAmount(order.total)}</span>
          </div>
        </div>
        
        <div class="footer">
          <p>¡Gracias por su preferencia!</p>
          <p>Vuelva pronto</p>
          <p style="margin-top: 5px; font-size: 9px;">${new Date().toLocaleTimeString('es-ES')}</p>
        </div>
        
        <script>
          window.onload = function() {
            window.print();
          }
        </script>
      </body>
      </html>
    `);
    
    printWindow.document.close();
    toast.success('Reimprimiendo recibo...');
  };

  const formatTime = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString('es-ES', { 
      hour: '2-digit', 
      minute: '2-digit',
      hour12: true 
    });
  };

  const getOrderTypeName = (type) => {
    const names = {
      'dine_in': 'En Local',
      'takeout': 'Para Llevar',
      'delivery': 'Delivery'
    };
    return names[type] || type;
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-xl font-bold">
            📋 Historial de Órdenes
          </DialogTitle>
        </DialogHeader>

        {/* Date Range Filter */}
        <div className="grid grid-cols-2 gap-4 mb-4">
          <div>
            <Label htmlFor="start-date" className="text-sm font-medium mb-1 flex items-center gap-2">
              <Calendar className="w-4 h-4" />
              Desde
            </Label>
            <Input
              id="start-date"
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="rounded-xl"
            />
          </div>
          <div>
            <Label htmlFor="end-date" className="text-sm font-medium mb-1 flex items-center gap-2">
              <Calendar className="w-4 h-4" />
              Hasta
            </Label>
            <Input
              id="end-date"
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="rounded-xl"
            />
          </div>
        </div>

        {/* Search by customer */}
        <div className="mb-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
            <Input
              placeholder="Buscar por nombre o teléfono del cliente..."
              value={customerSearch}
              onChange={(e) => setCustomerSearch(e.target.value)}
              className="pl-10 rounded-xl"
            />
          </div>
          {customerSearch && (
            <p className="text-sm text-gray-500 mt-2">
              {filteredOrders.length} orden(es) encontrada(s)
            </p>
          )}
        </div>

        {loading ? (
          <div className="text-center py-8">
            <p>Cargando historial...</p>
          </div>
        ) : filteredOrders.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <p>{customerSearch ? 'No se encontraron órdenes para este cliente' : 'No hay órdenes pagadas en el rango seleccionado'}</p>
          </div>
        ) : (
          <div className="space-y-3">
            {filteredOrders.map(order => (
              <div
                key={order.id}
                className="border rounded-lg p-4 hover:bg-gray-50 transition-colors"
              >
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="font-bold text-lg">
                        #{order.order_number}
                      </h3>
                      <span className="text-sm text-gray-600 flex items-center gap-1">
                        <Clock className="w-4 h-4" />
                        {formatTime(order.created_at)}
                      </span>
                    </div>
                    
                    <div className="grid grid-cols-2 gap-2 text-sm text-gray-700">
                      <div>
                        <span className="font-semibold">Tipo:</span>{' '}
                        {getOrderTypeName(order.order_type)}
                      </div>
                      <div>
                        <span className="font-semibold">Fecha:</span>{' '}
                        {new Date(order.updated_at).toLocaleDateString('es-ES', {
                          day: '2-digit',
                          month: '2-digit', 
                          year: 'numeric'
                        })} - {formatTime(order.updated_at)}
                      </div>
                      {order.table_name && (
                        <div>
                          <span className="font-semibold">Mesa:</span>{' '}
                          {order.table_name}
                        </div>
                      )}
                      {order.customer_name && (
                        <div>
                          <span className="font-semibold">Cliente:</span>{' '}
                          {order.customer_name}
                        </div>
                      )}
                      {order.cashier_name && (
                        <div>
                          <span className="font-semibold">Cajero:</span>{' '}
                          {order.cashier_name}
                        </div>
                      )}
                      <div className="flex items-center gap-1">
                        <DollarSign className="w-4 h-4" />
                        <span className="font-bold text-green-600">
                          ${formatAmount(order.total)}
                        </span>
                      </div>
                    </div>
                  </div>
                  
                  <Button
                    onClick={() => printReceipt(order, true)}
                    className="ml-4"
                    variant="outline"
                  >
                    <Printer className="w-4 h-4 mr-2" />
                    Reimprimir
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default OrderHistoryModal;
